import React, { ReactElement, memo } from 'react';
import { Button, Grid, Select, Tooltip, Paragraph, Checkbox, Icon, Divider } from '@dexma/ui-components';
import withLoading from 'adapter/in/common/hoc/withLoading';
import { Option, isSome } from 'fp-ts/Option';
import { DashboardUserGroup } from 'domain/entity/dashboard';
import i18n from 'asset/translation';
import {
    StyledUpperRow,
    StyledCenteredButton,
    StyledDividerWithMargin,
    StyledMiddleBlock,
    StyledUserGroupRow,
    StyledNotSharedMessage,
    StyledLowerRow,
    StyledDisclaimerMessage,
    StyledPublicDashboard,
    StyledDashboarShareModal,
} from 'adapter/in/dashboards/pages/Modals/ShareModal/style';
import ServerErrorAlert from 'adapter/in/dashboards/components/ServerErrorAlert';
import gtm from 'adapter/in/dashboards/config/gtm';
import {
    SHARE_OPTIONS,
    ShareMultiSelectorValue,
    ShareSelectorOption,
} from 'adapter/in/dashboards/components/DashboardActionsDropdown/hooks/actionsDropdown/useOnShareDashboardModal';
import { ErrorData } from 'domain/types';

export type DashboardShareModalProps = ShareModalCommonProps & {
    showModal: boolean;
    handleCloseModal: () => void;
    error: Option<ErrorData>;
    data: Option<DashboardUserGroup[]>;
    isLoading: boolean;
    errorAction: () => void;
};

export type ShareModalCommonProps = {
    userGroupsWithAccess: DashboardUserGroup[];
    isLoadingUpdate: boolean;
    handleAddUserGroupToList: () => void;
    handleUserGroupSelectChange: (multiSelectorValue: ShareMultiSelectorValue) => void;
    selectedUserGroupsToAggreggate: ShareMultiSelectorValue | undefined;
    handleSelectAccessType: (selectorValue: ShareSelectorOption) => void;
    selectedAccessType: ShareSelectorOption;
    handleCheckMakePublic: () => void;
    isDashboardPublic: boolean;
    hasDisclaimer: boolean;
    hasUpdateError: boolean;
    handleSaveShareChanges: () => void;
    handleOnChangeAccessType: (newAccess: string, groupId: string) => void;
    hasUpdateSuccess: boolean;
    isPinned?: boolean;
    handleCancel: () => void;
    tryAgain?: () => void;
};

type ShareModalContentProps = ShareModalCommonProps & { data: DashboardUserGroup[] };
const DashboardShareModal = memo(
    (props: DashboardShareModalProps): ReactElement => {
        const {
            userGroupsWithAccess,
            data,
            handleUserGroupSelectChange,
            handleAddUserGroupToList,
            hasUpdateError,
            selectedUserGroupsToAggreggate,
            handleSelectAccessType,
            selectedAccessType,
            handleCheckMakePublic,
            isDashboardPublic,
            isPinned,
            hasDisclaimer,
            handleSaveShareChanges,
            handleOnChangeAccessType,
            handleCancel,
            isLoadingUpdate,
            tryAgain,
            hasUpdateSuccess,
            showModal,
            handleCloseModal,
            isLoading,
            error,
            errorAction,
        } = props;

        return (
            <StyledDashboarShareModal
                width={700}
                visible={showModal}
                closable={isSome(error)}
                onCancel={handleCloseModal}
                title={i18n.t('dashboards.share.title')}
            >
                <ShareModalContent
                    errorAction={errorAction}
                    data={data}
                    isLoading={isLoading}
                    error={error}
                    handleUserGroupSelectChange={handleUserGroupSelectChange}
                    handleAddUserGroupToList={handleAddUserGroupToList}
                    hasUpdateError={hasUpdateError}
                    selectedUserGroupsToAggreggate={selectedUserGroupsToAggreggate}
                    handleSelectAccessType={handleSelectAccessType}
                    selectedAccessType={selectedAccessType}
                    handleCheckMakePublic={handleCheckMakePublic}
                    isDashboardPublic={isDashboardPublic}
                    isPinned={isPinned}
                    hasDisclaimer={hasDisclaimer}
                    handleSaveShareChanges={handleSaveShareChanges}
                    handleOnChangeAccessType={handleOnChangeAccessType}
                    handleCancel={handleCancel}
                    isLoadingUpdate={isLoadingUpdate}
                    tryAgain={tryAgain}
                    userGroupsWithAccess={userGroupsWithAccess}
                    hasUpdateSuccess={hasUpdateSuccess}
                />
            </StyledDashboarShareModal>
        );
    }
);

const ShareModalContent = withLoading((props: ShareModalContentProps) => {
    const {
        userGroupsWithAccess: sortedAccessGroups,
        data: aggreggableUserGroups,
        handleUserGroupSelectChange,
        handleAddUserGroupToList,
        hasUpdateError,
        selectedUserGroupsToAggreggate,
        handleSelectAccessType,
        selectedAccessType,
        handleCheckMakePublic,
        isDashboardPublic,
        isPinned,
        hasDisclaimer,
        handleSaveShareChanges,
        handleOnChangeAccessType,
        handleCancel,
        isLoadingUpdate,
        tryAgain,
        hasUpdateSuccess,
    } = props;

    return (
        <>
            <Grid>
                {hasUpdateError && tryAgain && <ServerErrorAlert tryAgain={tryAgain} dataId={gtm.dashboardAccess.actions.tryAgain} />}
                <StyledUpperRow data-testid='aggreggable-usergroups-option'>
                    <div style={{ width: '60%', marginRight: '8px' }}>
                        <Select
                            onChange={handleUserGroupSelectChange}
                            isMulti
                            value={selectedUserGroupsToAggreggate}
                            isSearchable
                            options={aggreggableUserGroups.map((userGroup) => ({
                                label: userGroup.name,
                                value: userGroup.id,
                                background: 'gray300',
                                color: 'white',
                            }))}
                        />
                    </div>
                    <div style={{ width: '20%', marginRight: '8px' }}>
                        <Select
                            value={selectedAccessType}
                            onChange={handleSelectAccessType}
                            options={[
                                {
                                    label: i18n.t('dashboards.share.view'),
                                    value: SHARE_OPTIONS.view,
                                },
                                {
                                    label: i18n.t('dashboards.share.edit'),
                                    value: SHARE_OPTIONS.edit,
                                },
                            ]}
                        />
                    </div>
                    <StyledCenteredButton>
                        <Button
                            isDisabled={aggreggableUserGroups.length === 0 || selectedUserGroupsToAggreggate?.length === 0}
                            onClick={handleAddUserGroupToList}
                            text={
                                selectedUserGroupsToAggreggate && selectedUserGroupsToAggreggate.length > 1
                                    ? i18n.t('dashboards.share.addGroups')
                                    : i18n.t('dashboards.share.addGroup')
                            }
                        />
                    </StyledCenteredButton>
                </StyledUpperRow>
            </Grid>
            <Divider />
            <StyledMiddleBlock>
                <UserGroupList userGroupsWithAccess={sortedAccessGroups} handleOnChangeAccessType={handleOnChangeAccessType} />
            </StyledMiddleBlock>
            {hasDisclaimer && <DisclaimerMessage />}
            <StyledLowerRow>
                <StyledPublicDashboard data-testid='make-dashboard-public-toggle'>
                    {isPinned ? (
                        <Tooltip style={{ display: 'flex' }} position='top' title={i18n.t('dashboards.pinned.tooltip.share')}>
                            <Checkbox disabled={isPinned} checked={isDashboardPublic} onChange={handleCheckMakePublic} />
                            <Paragraph style={{ marginLeft: '8px' }}>{i18n.t('dashboards.share.makePublic')}</Paragraph>
                        </Tooltip>
                    ) : (
                        <>
                            <Checkbox checked={isDashboardPublic} onChange={handleCheckMakePublic} />
                            <Paragraph style={{ marginLeft: '8px' }}>{i18n.t('dashboards.share.makePublic')}</Paragraph>
                        </>
                    )}
                    <Tooltip style={{ display: 'flex' }} position='right' title={i18n.t('dashboards.share.makePublicTooltip')}>
                        <Icon style={{ marginLeft: '2px' }} size={14} name='circle_info_outline' />
                    </Tooltip>
                </StyledPublicDashboard>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        style={{ marginRight: '16px' }}
                        size='large'
                        data-testid='save-button'
                        dataId={gtm.dashboardAccess.actions.save}
                        iconBefore={hasUpdateSuccess ? 'check' : ''}
                        onClick={handleSaveShareChanges}
                        isLoading={isLoadingUpdate}
                    >
                        {i18n.t('dashboards.share.save')}
                    </Button>
                    <Button
                        size='large'
                        variant='secondary'
                        data-testid='cancel-button'
                        dataId={gtm.dashboardAccess.actions.cancelSave}
                        onClick={handleCancel}
                    >
                        {i18n.t('dashboards.share.cancel')}
                    </Button>
                </div>
            </StyledLowerRow>
        </>
    );
});

type UserGroupListProps = {
    userGroupsWithAccess: DashboardUserGroup[];
    handleOnChangeAccessType: (newAccess: string, groupId: string) => void;
};
const UserGroupList = (props: UserGroupListProps) => {
    const { userGroupsWithAccess, handleOnChangeAccessType } = props;

    const alphabetically = (groupA: DashboardUserGroup, groupB: DashboardUserGroup) => {
        if (groupA.name < groupB.name) {
            return -1;
        }
        return 1;
    };

    const mapLabel = (value: string) => {
        switch (value) {
            case SHARE_OPTIONS.view:
                return i18n.t('dashboards.share.view');
            case SHARE_OPTIONS.remove:
                return i18n.t('dashboards.share.remove');
            case SHARE_OPTIONS.edit:
            default:
                return i18n.t('dashboards.share.edit');
        }
    };
    const rows =
        userGroupsWithAccess.length > 0 ? (
            userGroupsWithAccess.sort(alphabetically).map((group: DashboardUserGroup) => (
                <React.Fragment key={group.id}>
                    <StyledUserGroupRow>
                        <div>{group.name}</div>
                        <div style={{ width: '25%' }}>
                            <Select
                                onChange={(e: ShareSelectorOption) => {
                                    handleOnChangeAccessType(e.value, group.id);
                                }}
                                value={{ label: mapLabel(group.level), value: group.level }}
                                options={[
                                    { label: mapLabel(SHARE_OPTIONS.view), value: SHARE_OPTIONS.view },
                                    { label: mapLabel(SHARE_OPTIONS.edit), value: SHARE_OPTIONS.edit },
                                    { label: mapLabel(SHARE_OPTIONS.remove), value: SHARE_OPTIONS.remove },
                                ]}
                            />
                        </div>
                    </StyledUserGroupRow>
                    <Divider />
                </React.Fragment>
            ))
        ) : (
            <>
                <StyledNotSharedMessage>{i18n.t('dashboards.share.notShared')}</StyledNotSharedMessage>
                <StyledDividerWithMargin />
            </>
        );

    return <Grid data-testid='user-group-list'>{rows}</Grid>;
};

const DisclaimerMessage = () => (
    <StyledDisclaimerMessage>
        <div style={{ padding: '0px 12px' }}>
            <Icon size={20} style={{ marginRight: '8px', marginBottom: '-4px' }} name='warning' />
            {i18n.t('dashboards.share.editDisclaimer')}
        </div>
    </StyledDisclaimerMessage>
);

export default DashboardShareModal;
